import React from 'react';
import { computed, makeObservable } from 'mobx';
import { observer } from 'mobx-react';

import { ComponentBase, InjectedStyledComponent } from '../../../../../shared/index.js';
import { ITableProps } from '../../../Table/models/index.js';
import { ITableRow } from '../../../Table/models/ITableRow.js';
import { ITableGrouping } from '../../models/ITableGrouping.js';
import { ITableGroupingGroup } from '../../models/ITableGroupingGroup.js';
import Group from '../Group/Group.js';

import { IGroupsConfiguration, IGroupsProps, IGroupsState } from './models/index.js';
import { GROUPS_CATEGORY_KEY, GROUPS_COMPONENT_KEY, GROUPS_PARENT_COMPONENT_KEY } from './Groups.meta.js';

export class Groups extends ComponentBase<IGroupsProps, IGroupsState, IGroupsConfiguration> {
	categoryKey = GROUPS_CATEGORY_KEY;
	componentKey = GROUPS_COMPONENT_KEY;
	get parentComponentKey(): string | undefined {
		return GROUPS_PARENT_COMPONENT_KEY;
	}

	constructor(props: IGroupsProps) {
		super(props);

		makeObservable(this, {
			parentGrouping: computed,
			grouping: computed,
			tableProps: computed,
			values: computed,
			rows: computed,
			groups: computed,
		});
	}

	get parentGrouping(): ITableGrouping | undefined {
		return this.props.parentGrouping;
	}

	get grouping(): ITableGrouping {
		const tableProps: ITableProps =
			this.props.grouping.tableProps || (this.parentGrouping && this.parentGrouping.tableProps) || {};
		const onToggleChange =
			(this.props.grouping.toggle && this.props.grouping.toggle.onToggleChange) ||
			(this.parentGrouping && this.parentGrouping.toggle && this.parentGrouping.toggle.onToggleChange);
		return {
			...this.props.grouping,
			tableProps,
			onGroupSelectionChange:
				this.props.grouping.onGroupSelectionChange ||
				(this.parentGrouping && this.parentGrouping.onGroupSelectionChange),
			toggle: {
				...(this.props.grouping.toggle || {}),
				onToggleChange,
			},
		};
	}

	get tableProps(): ITableProps {
		return {
			...this.grouping.tableProps,
			rows: this.rows,
		};
	}

	get values(): any[] {
		const { key } = this.grouping;
		return Array.from(new Set(this.rows.map((row) => row[key])).values());
	}

	get rows(): ITableRow[] {
		const { rows = [] } = this.props;
		return rows;
	}

	get groups(): ITableGroupingGroup[] {
		return this.props.groups || [];
	}

	getGroup = (value: any) => {
		return this.groups.find((group) => group.value === value);
	};

	render() {
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const {
			children,
			configuration,
			groups,
			grouping,
			suppressAnalytics,
			analyticsContext,
			parentGrouping,
			...otherProps
		} = this.props;
		return (
			<section {...otherProps}>
				{this.values.map((value, index) => {
					const group = this.getGroup(value);
					if (!group) {
						return null;
					}
					return (
						<Group
							key={`key_${value}_${index}`}
							value={value}
							grouping={this.grouping}
							group={group}
							rows={this.rows}
						/>
					);
				})}
			</section>
		);
	}
}

export default InjectedStyledComponent(observer(Groups), GROUPS_CATEGORY_KEY, GROUPS_PARENT_COMPONENT_KEY, [
	GROUPS_COMPONENT_KEY,
]);
